import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BodyContainer, MainContainer } from './PreviewInventory.styles';
import {
  Button,
  Row,
  Col,
  Divider,
  Table,
  Input,
  Form,
  Alert,
  InputNumber,
  Select,
  Popconfirm,
  Icon,
  Checkbox,
  notification,
  message,
  Modal,
  Tooltip,
  Tabs,
  Tag,
  Switch
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link, withRouter } from 'react-router-dom';
import { buildAPBillingInvoiceUri } from 'utils/routes';
import moment from 'moment';
import { useFetchConstant } from 'hooks/constants';
import { getHostInventoryForAdminInSinglePull } from 'utils/apis/billingInventory';
import { getHostMonthlyAddOnRecord } from 'utils/apis/subscriptionAddOnProduct';
import { formatToDateString } from 'utils/date';
import { getConstantLabel, guard, capitalizeFirstLetter } from 'utils/general';
import { constructColumn, constructColumnFilterRadio, constructColumnFilterSearch } from 'utils/table/table';
import { putUpdateUnitConfig } from 'utils/apis/unit';
import { getAllProduct } from 'utils/apis/configProduct';
import { getHostById, getPricingModelByHostId, updateHost } from 'utils/apis/host';
import { getAllDiscount, getDiscount } from 'utils/apis/configDiscount';
import {
  createInvoice,
  getInvoiceById,
  getLatestHostProtectRefNumber,
  updateInvoice,
  getInvoicePDFPreview,
  getSnapshotValidationByInvoiceId,
  getPrevMonthlyPrebuy
} from 'utils/apis/billingInvoice';
import { MONTHS, billingFunctions, HOST_CONNECT_OTA_OPTIONS } from 'utils/constants';
import { attachmentHandler } from 'utils/apis/attachment';

const { TabPane } = Tabs;
const { Option } = Select;

const useFetchConstants = () => {
  const { selection: unitSourceSelection, isLoading: isUnitSourceLoading } = useFetchConstant('unitSource');
  const { selection: unitTypes, isLoading: isUnitTypesLoading } = useFetchConstant('unitType');
  const { selection: planSelection, isLoading: isPlanSelectionLoading } = useFetchConstant('plan');
  const { selection: subscriptionModes, isLoading: isSubscriptionModesLoading } = useFetchConstant('subscriptionMode');
  const isLoadingConstants = useMemo(() => isUnitSourceLoading || isUnitTypesLoading || isPlanSelectionLoading || isSubscriptionModesLoading, [
    isUnitSourceLoading,
    isUnitTypesLoading,
    isPlanSelectionLoading,
    isSubscriptionModesLoading
  ]);

  return { isLoadingConstants, unitSourceSelection, unitTypes, planSelection, subscriptionModes };
};

const PreviewInventory = ({ match, form, location, history }) => {
  const { getFieldDecorator, validateFields, setFieldsValue, getFieldValue } = form;
  const { id, monthWithYear } = match.params;
  const { formattedInvoice, isEditClicked, record, hostEmail } = location.state || {};

  const [query, setQuery] = useState({});
  const [remarks, setRemarks] = useState({});
  const [changedRemarks, setChangedRemarks] = useState({});
  const [subscriptionFees, setSubscriptionFees] = useState({});
  const [alerts, setAlerts] = useState({});
  const [addOnRecordMissing, setAddOnRecordMissing] = useState(false);
  const [addOnStatuses, setAddOnStatuses] = useState({});
  const [originalSubscriptionFees, setOriginalSubscriptionFees] = useState({});
  const [modifiedSubscriptionFees, setModifiedSubscriptionFees] = useState({});
  const [discount, setDiscount] = useState([]);
  const [waive, setWaive] = useState([]);
  const [isCurrentMonth, setIsCurrentMonth] = useState(false);
  const [invoiceNumberValidation, setInvoiceNumberValidation] = useState(true);
  const [invoiceRemark, setInvoiceRemark] = useState('');
  const [invoiceAccount, setInvoiceAccount] = useState('');
  const [manuallyCreatedUnitForHostProtect, setManuallyCreatedUnitForHostProtect] = useState([]);
  const [hostProtectInvoiceRef, setHostProtectInvoiceRef] = useState(0);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [invoiceSnapshot, setInvoiceSnapshot] = useState({});
  const [activeTabKey, setActiveTabKey] = useState('additionalProduct');
  const [mainTabKey, setMainTabKey] = useState('inventories');
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const [hostInvoice, setHostInvoice] = useState(null);
  const [isAllWaived, setIsAllWaived] = useState(false);
  const [discountSelections, setDiscountSelections] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [subscriptionAddOnProductData, setSubscriptionAddOnProductData] = useState([]);
  const [unifiedInboxStatus, setUnifiedInboxStatus] = useState('inactive'); // 'active' or 'inactive'
  const [initialUnifiedInboxStatus, setInitialUnifiedInboxStatus] = useState('inactive'); // Track initial status — derived from add-on record

  const [host, setHost] = useState();
  const [pricingModel, setPricingModel] = useState([]);
  const [productData, setProductData] = useState([]);
  const [systemProduct, setSystemProduct] = useState([]);
  const [productFields, setProductFields] = useState([]);
  const [counter, setCounter] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState();
  const [totalAmount, setTotalAmount] = useState(0);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [showGenerateAlert, setGenerateAlert] = useState(false);
  const [showGenerateFailedAlert, setGenerateFailedAlert] = useState(false);
  const [generateFailedMessage, setGenerateFailedMessage] = useState('');

  const blobRef = useRef(null);

  const [pdfFileName, setPdfFileName] = useState();
  const [generatedPDF, setGeneratedPDF] = useState(false);
  const [inventories, setInventories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false); // Prebuy tab state variables
  const [prebuyFields, setPrebuyFields] = useState([]);
  const [prebuyCounter, setPrebuyCounter] = useState(0);
  const [selectedPrebuyProducts, setSelectedPrebuyProducts] = useState([]);

  const { isLoadingConstants, unitSourceSelection, unitTypes, planSelection } = useFetchConstants();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const formatParentPrimary = useCallback((parent, primary) => {
    const parentUnitName = guard(() => parent.name, '');
    const primaryUnitName = guard(() => primary.name, '');
    return `${parentUnitName}${primaryUnitName}` || undefined;
  }, []);

  const formatRenewalDate = useCallback((renewalDate, isMonthlySubscription) => {
    return isMonthlySubscription ? 'Monthly' : formatToDateString(renewalDate);
  }, []);

  const postProcessHostInventory = result => {
    const formattedInventoryStatuses = result.inventories.map(inventoryStatus => ({
      ...inventoryStatus,
      parentPrimaryUnitName: formatParentPrimary(inventoryStatus.unitParent, inventoryStatus.unitPrimary),
      planNameLabel: getConstantLabel(planSelection, inventoryStatus.planName),
      unitConfig: {
        ...inventoryStatus.unitConfig,
        renewal: formatRenewalDate(inventoryStatus.unitConfig.renewalDate, inventoryStatus.unitConfig.isMonthlySubscription),
        unitTypeLabel: getConstantLabel(unitTypes, inventoryStatus.unitConfig.unitType)
      },
      unitSourceLabel: getConstantLabel(unitSourceSelection, inventoryStatus.unitSource)
    }));
    return { ...result, inventories: formattedInventoryStatuses };
  };

  // Function to map pricingModel.pricingTable.products to systemProduct state
  const mapSystemProducts = (pricingModel, productData) => {
    if (!pricingModel || !pricingModel.pricingTable || !pricingModel.pricingTable.products || !productData) {
      return [];
    }

    try {
      const mappedProducts = pricingModel.pricingTable.products
        .map(pricingTableProduct => {
          // Find the matching product from productData
          const productDetails = productData.find(product => product._id.toString() === pricingTableProduct.product.toString());

          if (!productDetails) {
            return null;
          } // Create a structured product object that combines pricing table info with product details
          const mappedProduct = {
            ...productDetails
          };

          return mappedProduct;
        })
        .filter(product => product !== null); // Remove any null entries

      return mappedProducts;
    } catch (error) {
      console.error('Error mapping system products:', error);
      return [];
    }
  };

  const fetchProducts = async () => {
    try {
      const { data: pricingModel } = await getPricingModelByHostId(id);
      const { data: products } = await getAllProduct();

      const productIds = pricingModel.otherProducts.map(product => product.product);
      const subscriptionAddOnProductIds = pricingModel.subscriptionAddOnPricingTable.map(product => product.subscriptionAddOnProduct);
      const productData = products.filter(product => productIds.includes(product._id));

      // Fetch add-on product records from the new collection using the previous month
      // (subtract 1 month, consistent with inventory data which is pulled for the prior month)
      const prevMonthWithYear = moment(monthWithYear, 'YYYY-MM')
        .subtract(1, 'months')
        .format('YYYY-MM');
      const addOnRecordRes = await getHostMonthlyAddOnRecord(id, prevMonthWithYear).catch(() => null);
      console.log(addOnRecordRes, 31);

      // Temporary: keep this false even when record is missing, for now !
      // if got record then only we will check in future !
      setAddOnRecordMissing(false);
      const addOnProducts = (addOnRecordRes && addOnRecordRes.data && addOnRecordRes.data.addOnProducts) || [];

      // Build the full list of add-on product IDs: from pricingTable + from the monthly record
      const recordAddOnProductIds = addOnProducts.map(addOn => addOn.product);
      const allAddOnProductIds = [...new Set([...subscriptionAddOnProductIds, ...recordAddOnProductIds])];
      const subscriptionAddOnProductData = products.filter(product => allAddOnProductIds.includes(product._id));

      // Determine unified inbox status from the monthly record's addOnProducts
      // Active if activatedDate exists and is after (or equal to) deactivatedDate for any add-on product
      const unifiedInboxAddOn = addOnProducts.find(addOn => {
        const product = products.find(p => p._id === addOn.product);
        return !!product;
      });

      let resolvedStatus = 'inactive';
      if (unifiedInboxAddOn) {
        const activatedDate = unifiedInboxAddOn.activatedDate ? new Date(unifiedInboxAddOn.activatedDate) : null;
        const deactivatedDate = unifiedInboxAddOn.deactivatedDate ? new Date(unifiedInboxAddOn.deactivatedDate) : null;
        if (activatedDate && (!deactivatedDate || activatedDate > deactivatedDate)) {
          resolvedStatus = 'active';
        }
      }

      setUnifiedInboxStatus(resolvedStatus);
      setInitialUnifiedInboxStatus(resolvedStatus);

      // Initialize addOnStatuses for all non-Unified-Inbox subscription add-on products
      const initialAddOnStatuses = {};
      addOnProducts.forEach(addOn => {
        const product = products.find(p => p._id === addOn.product);
        if (!product) return;
        // Skip Unified Inbox — it has its own dedicated state
        if ((product.name || '').toLowerCase().includes('unified inbox')) return;

        let status = 'inactive';

        const activatedDate = addOn.activatedDate ? new Date(addOn.activatedDate) : null;
        const deactivatedDate = addOn.deactivatedDate ? new Date(addOn.deactivatedDate) : null;
        console.log(activatedDate, deactivatedDate, 515);
        if (activatedDate && (!deactivatedDate || activatedDate > deactivatedDate)) {
          status = 'active';
        }

        initialAddOnStatuses[addOn.product] = status;
      });
      setAddOnStatuses(initialAddOnStatuses);

      setProductData(productData);
      setSubscriptionAddOnProductData(subscriptionAddOnProductData);

      // Map system products from pricingModel.pricingTable.products to systemProduct state
      const mappedSystemProducts = mapSystemProducts(pricingModel, products);
      setSystemProduct(mappedSystemProducts);

      console.log('Mapped system products:', mappedSystemProducts);
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  };

  const padInvoiceNumber = value => {
    const match = value.match(/^(\d+)(-?[A-Za-z]*)$/); // Matches digits followed by optional hyphen and letters
    if (match) {
      const numericPart = match[1];
      const letterPart = match[2] || '';
      const paddedNumericPart = numericPart.padStart(3, '0');
      return `${paddedNumericPart}${letterPart}`;
    }
    return value;
  };

  useEffect(() => {
    if (!isLoadingConstants && id && monthWithYear) {
      const fetchInventoryStatuses = async () => {
        setIsLoading(true);
        const pricingModel = await getPricingModelByHostId(id);
        const host = await getHostById(id);
        if (record) {
          const hasSnapshot = await getSnapshotValidationByInvoiceId(record._id);
          setHasSnapshot(hasSnapshot.hasSnapshot);
        }
        try {
          let invoiceRef = null;
          let invoiceRemark;
          let invoiceAccount;
          let monthWithYearPassed = monthWithYear;
          setInvoiceFiles(formattedInvoice.files);
          const { refNumber } = await getLatestHostProtectRefNumber();
          const discountSelections = await getAllDiscount();
          setHostProtectInvoiceRef(refNumber);
          if (isEditClicked) {
            const invoice = await getInvoiceById(record._id);
            if (formattedInvoice.invoiceNo) {
              invoiceRef = formattedInvoice.invoiceNo;
              setInvoiceNo(formattedInvoice.invoiceNo);
            } else {
              invoiceRef = invoice.invoiceNo;
              setInvoiceNo(invoice.invoiceNo);
            }
            setTotalAmount(invoice.amount);
          } else {
            if (host.data.invoiceNumber) {
              const paddedInvoiceNumber = padInvoiceNumber(String(host.data.invoiceNumber)); // Ensure it's a string
              const formattedMonthYear = moment(monthWithYear, 'YYYY-MM').format('YYMM'); // "2502" (or for the current month/year)
              invoiceRef = `PINV-${formattedMonthYear}-${paddedInvoiceNumber}`;
            }
            setInvoiceNo(invoiceRef);
          }
          if (host.data.invoiceRemark) {
            invoiceRemark = host.data.invoiceRemark;
          }
          if (host.data.invoiceAccount) {
            invoiceAccount = host.data.invoiceAccount;
          }
          setInvoiceRemark(invoiceRemark);
          setInvoiceAccount(invoiceAccount);

          monthWithYearPassed = moment(monthWithYear, 'YYYY-MM')
            .subtract(1, 'months')
            .format('YYYY-MM');

          const result = await getHostInventoryForAdminInSinglePull(monthWithYearPassed, id, query, true, postProcessHostInventory);
          // console.log(result, 1);
          const prevMonth = moment(monthWithYear, 'YYYY-MM').subtract(1, 'month');
          const currentYearMonth = moment(monthWithYear, 'YYYY-MM');

          //If monthWithYear is April, then we getting March de data ma, then if unit deleted on March then still charge de ma, follow back monthly ?
          // console.log('Previous Month:', prevMonth.format('YYYY-MM'));
          // console.log('Current Year Month:', currentYearMonth.format('YYYY-MM'));
          const filteredInventories = result.inventories.filter(inventory => {
            if (pricingModel.data.subscriptionType === 'yearly') {
              if (inventory.isUnitDeleted) {
                const deletedAt = inventory.unitDeletedAt;
                if (!deletedAt) return false;

                const deletedMonth = moment(deletedAt);
                return deletedMonth.isAfter(prevMonth, 'month'); // Only include if deleted AFTER current year month
              }
              return true; // Not deleted, include
            } else {
              if (inventory.isUnitDeleted) {
                const deletedAt = inventory.unitDeletedAt;
                if (!deletedAt) return false;

                const deletedMonth = moment(deletedAt);
                return deletedMonth.isAfter(prevMonth, 'month'); // Only include if deleted AFTER previous month
              }
            }

            return true; // Not deleted, include
          });

          if (pricingModel.data.subscriptionType === 'monthly') {
            const monthlyPrebuyUnits = await getPrevMonthlyPrebuy(id, monthWithYear);

            if (Array.isArray(monthlyPrebuyUnits) && monthlyPrebuyUnits.length > 0) {
              const filteredPrebuyUnits = monthlyPrebuyUnits
                .map(prebuy => {
                  // Find all matching inventories for this planName (case-insensitive)
                  const matchingInventories = result.inventories.filter(
                    inv => inv.planName && prebuy.planName && inv.planName.toLowerCase() === prebuy.planName.toLowerCase()
                  );

                  // Count how many inventories were created last month
                  const prevMonth = moment(monthWithYear, 'YYYY-MM').subtract(1, 'month');
                  const countCreatedLastMonth = matchingInventories.reduce((count, inv) => {
                    if (inv.unitCreatedAt && moment(inv.unitCreatedAt).isSame(prevMonth, 'month')) {
                      return count + 1;
                    }
                    return count;
                  }, 0);

                  // Adjust quantity for each matching inventory created last month
                  const adjustedQuantity = Math.max(0, prebuy.quantity - countCreatedLastMonth);

                  return { ...prebuy, quantity: adjustedQuantity };
                })
                .filter(prebuy => prebuy.quantity > 0); // Remove units with quantity 0

              // Update form state
              setPrebuyFields(filteredPrebuyUnits.map((_, idx) => idx));
              setPrebuyCounter(filteredPrebuyUnits.length); // Ensure prebuyCounter is in sync with backend units
              filteredPrebuyUnits.forEach((unit, idx) => {
                form.setFieldsValue({
                  [`prebuy_product[${idx}]`]: { key: unit.productId, label: unit.subscriptionName },
                  [`prebuy_quantity[${idx}]`]: unit.quantity
                });
              });
            }
          }

          if (host.data.discount) {
            const discount = await getDiscount(host.data.discount);
            setDiscount(discount.data);
          }
          setHostInvoice(invoiceRef);
          // console.log(result);
          setInvoiceNumberValidation(result.invoiceNumberValidation);
          setHost(host.data);
          setInventories(filteredInventories);
          setPricingModel(pricingModel.data);
          setDiscountSelections([{ _id: 'none', name: 'None' }, ...discountSelections.data]);
          setFieldsValue({
            hostField: host.data.name // Assuming host.data has a 'name' property
            // Add other form fields here as needed
          });
        } catch (error) {
          console.error('Error fetching inventory statuses:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchInventoryStatuses();
      fetchProducts();
    }
  }, [isLoadingConstants, monthWithYear, id]);

  useEffect(() => {
    if (!isLoadingConstants && id && monthWithYear) {
      const currentMonthWithYear = moment().format('YYYY-MM');
      if (monthWithYear === currentMonthWithYear) {
        setIsCurrentMonth(true);
      } else {
        setIsCurrentMonth(false);
      }
    }
  }, [isLoadingConstants, id, monthWithYear]);

  useEffect(() => {
    if (inventories.length > 0) {
      const initialRemarks = inventories.reduce((acc, inventory) => {
        acc[inventory.unitId] = inventory.unitConfig.remark || '';
        return acc;
      }, {});
      setRemarks(initialRemarks);

      const initialSubscriptionFees = inventories.reduce((acc, inventory) => {
        acc[inventory.unitId] = inventory.unitConfig.subscriptionFee || '';
        return acc;
      }, {});
      setOriginalSubscriptionFees(initialSubscriptionFees);
      setSubscriptionFees(initialSubscriptionFees);
    }
  }, [inventories]);

  const handleRemarkChange = (unitId, value) => {
    setRemarks(prevRemarks => ({
      ...prevRemarks,
      [unitId]: value
    }));

    setChangedRemarks(prevChangedRemarks => ({
      ...prevChangedRemarks,
      [unitId]: value
    }));
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = async sendEmail => {
    setIsModalVisible(false);
    await handleSaveInvoice(sendEmail);
    history.push(buildAPBillingInvoiceUri());
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const uploadPDFFile = async (file, fileName) => {
    try {
      // Create a new File object with the correct filename
      const renamedFile = new File([file], fileName, { type: 'application/pdf' });

      const response = await attachmentHandler(renamedFile, 'invoices');

      console.log(response, 31);

      if (response && response.uploadedFile) {
        // Assuming response contains a URL or file link
        return response.uploadedFile;
      } else {
        throw new Error(response.message || 'File upload failed');
      }
    } catch (error) {
      console.error('Error uploading PDF file:', error);
      throw error;
    }
  };

  const handleSubscriptionFeeChange = (unitId, value) => {
    setSubscriptionFees(prevFees => ({
      ...prevFees,
      [unitId]: value
    }));

    // Compare against the original value
    const originalValue = String(originalSubscriptionFees[unitId] || '');
    const currentValue = String(value || '');
    if (currentValue !== originalValue) {
      setAlerts(prevAlerts => ({
        ...prevAlerts,
        [unitId]: true
      }));
      // Add to modified subscription fees
      setModifiedSubscriptionFees(prevModified => ({
        ...prevModified,
        [unitId]: value
      }));
    } else {
      setAlerts(prevAlerts => {
        const { [unitId]: _, ...rest } = prevAlerts; // Remove alert
        return rest;
      });
      // Remove from modified subscription fees
      setModifiedSubscriptionFees(prevModified => {
        const { [unitId]: _, ...rest } = prevModified;
        return rest;
      });
    }
  };

  const hasParentChildHostRelation = inventories => {
    // console.log(inventories);
    for (const inventory of inventories) {
      if (inventory.hostId !== id) {
        return true;
      }
    }
    return false;
  };

  const handleGenerate = async () => {
    try {
      setIsLoading(true);
      validateFields(async (err, values) => {
        if (!err) {
          for (const [unitId, newRemark] of Object.entries(changedRemarks)) {
            const inventoryItem = inventories.find(item => item.unitId === unitId);

            if (inventoryItem) {
              const unitConfig = {
                unitType: inventoryItem.unitConfig.unitType,
                renewalDate: inventoryItem.unitConfig.renewalDate || null,
                subscriptionMode: inventoryItem.unitConfig.subscriptionMode,
                subscriptionFee: inventoryItem.unitConfig.subscriptionFee,
                isFree: inventoryItem.unitConfig.isFree,
                primary: inventoryItem.unitConfig.primary || null,
                isActive: inventoryItem.unitConfig.isActive,
                remark: newRemark
              };
              // await putUpdateUnitConfig(unitId, unitConfig);
            } else {
              console.warn(`Inventory item with unitId ${unitId} not found.`);
            }
          }
          console.log('All units updated successfully');

          // Generate PDF data
          const date = formattedInvoice.invoiceDate ? moment(formattedInvoice.invoiceDate).format('D/M/YYYY') : moment().format('D/M/YYYY');
          let invoiceRef = invoiceNo;
          const remark = form.getFieldValue(`invoiceRemark`);
          const excludeSystemSubscription = form.getFieldValue('excludeSystemSubscription') || false;
          const chargeableChild = pricingModel.chargeableChild;
          const modifiedSubFee = subscriptionFees;
          const discountApplied = discount;
          const subscriptionType = pricingModel.subscriptionType;
          const subscriptionMonth = host.subscriptionMonth;
          const hostName = host.name;
          const waivedUnit = Object.keys(waive).filter(unitId => waive[unitId]);
          const hasParentChildHost = hasParentChildHostRelation(inventories);
          const prebuyUnits = prebuyFields.map(index => ({
            productId: form.getFieldValue(`prebuy_product[${index}]`).key,
            subscriptionName: form.getFieldValue(`prebuy_product[${index}]`).label,
            quantity: form.getFieldValue(`prebuy_quantity[${index}]`)
          }));
          console.log('Prebuy Units:', prebuyUnits);
          // console.log(waive, 55);
          const updatedInventories = inventories.map(inventory => {
            let updatedInventory = { ...inventory };
            // Rename unitPrimary to unitParent if it exists
            if ('unitPrimary' in updatedInventory) {
              updatedInventory.unitParent = updatedInventory.unitPrimary;
              delete updatedInventory.unitPrimary;
            }

            // Update remark if unitId exists in changedRemarks
            if (changedRemarks.hasOwnProperty(inventory.unitId)) {
              updatedInventory = {
                ...updatedInventory,
                unitConfig: {
                  ...updatedInventory.unitConfig,
                  remark: changedRemarks[inventory.unitId]
                }
              };
            }

            // Update subscriptionFee if unitId exists in modifiedSubFee
            if (subscriptionFees.hasOwnProperty(inventory.unitId)) {
              updatedInventory = {
                ...updatedInventory,
                unitConfig: {
                  ...updatedInventory.unitConfig,
                  subscriptionFee: subscriptionFees[inventory.unitId]
                }
              };
            }

            return updatedInventory;
          });

          const additionalProducts = productFields.map(index => ({
            code: form.getFieldValue(`otherProducts[${index}]`).key,
            name: form.getFieldValue(`otherProducts[${index}]`).label,
            quantity: form.getFieldValue(`quantity[${index}]`)
          }));

          const product = productData.find(product => product.categoryParent.includes('hostProtect'));
          const code = product ? product._id : null; // Return the _id if the product is found, otherwise return null
          let hostProtectProducts = null;
          let manualUnit = null;
          let allUnitsForHostProtect = null;
          let activeTabKeyValue = activeTabKey;
          let additionalProductDiscount = form.getFieldValue('discountApplied') || [];

          if (excludeSystemSubscription) {
            if (additionalProductDiscount) {
              if (additionalProductDiscount.key === 'none') {
                additionalProductDiscount = [];
              } else {
                const matchedDiscount = discountSelections.find(discount => discount._id === additionalProductDiscount.key);
                additionalProductDiscount = matchedDiscount || [];
              }
            }

            if (getFieldValue('unitForHostProtect')) {
              hostProtectProducts = getFieldValue('unitForHostProtect').map(unit => ({
                code: code,
                unitName: unit.label, // This will represent the unit name
                propertyName: form.getFieldValue(`propertyName[${unit.key}]`),
                ownerName: form.getFieldValue(`ownerName[${unit.key}]`), // Fetch owner name for each unit
                icOwner: form.getFieldValue(`icOwner[${unit.key}]`), // Fetch IC owner for each unit
                address: form.getFieldValue(`address[${unit.key}]`) // Fetch address for each unit
              }));
            }
            if (manuallyCreatedUnitForHostProtect) {
              manualUnit = manuallyCreatedUnitForHostProtect.map(unit => ({
                code: code,
                unitName: form.getFieldValue(`unitName[${unit.key}]`), // This will represent the unit name
                propertyName: form.getFieldValue(`propertyName[${unit.key}]`),
                ownerName: form.getFieldValue(`ownerName[${unit.key}]`), // Fetch owner name for each unit
                icOwner: form.getFieldValue(`icOwner[${unit.key}]`), // Fetch IC owner for each unit
                address: form.getFieldValue(`address[${unit.key}]`) // Fetch address for each unit
              }));
            }
            allUnitsForHostProtect = [...(hostProtectProducts || []), ...(manualUnit || [])];
            if (allUnitsForHostProtect.length > 0 && activeTabKey === 'hostProtect') {
              if (isEditClicked && formattedInvoice.invoiceNo && formattedInvoice.invoiceNo.includes('PINV-HP')) {
                invoiceRef = formattedInvoice.invoiceNo;
                // console.log(invoiceRef, 8);
              } else {
                const paddedRefNumber = String(hostProtectInvoiceRef).padStart(3, '0');
                invoiceRef = `PINV-HP-${moment(monthWithYear).format('YYMM')}-${paddedRefNumber}`;
              }
            } else {
              if (host.additionalProductInvoiceNumber) {
                const paddedRefNumber = String(host.additionalProductInvoiceNumber).padStart(3, '0');
                invoiceRef = `PINV-SVC-${moment(monthWithYear).format('YYMM')}-${paddedRefNumber}`;
              }
            }
            setInvoiceNo(invoiceRef);
          } else {
            if (host.invoiceNumber) {
              invoiceRef = `PINV-${moment(monthWithYear).format('YYMM')}-${padInvoiceNumber(String(host.invoiceNumber))}`; // Ensure it's a string
            }
            setInvoiceNo(invoiceRef);
          }

          // if (updatedInventories.length === 0 && excludeSystemSubscription === false) {
          //   setIsLoading(false);
          //   notification.error({
          //     message: 'Invoice Generation Error',
          //     description: 'There are no units to generate invoice for.'
          //   });
          //   return;
          // }
          if (excludeSystemSubscription) {
            if (allUnitsForHostProtect.length === 0) {
              activeTabKeyValue = 'additionalProduct';
            }
            if (additionalProducts.length === 0) {
              activeTabKeyValue = 'hostProtect';
            }
          }
          const res = await getInvoicePDFPreview({
            host: host._id,
            invoiceRemark: form.getFieldValue('invoiceRemark'),
            invoiceAccount: form.getFieldValue('invoiceAccount'),
            updatedInventories,
            additionalProducts,
            hostProtectProducts: allUnitsForHostProtect,
            date,
            monthWithYear,
            invoiceRef,
            remark,
            excludeSystemSubscription,
            chargeableChild,
            modifiedSubFee,
            discountApplied: excludeSystemSubscription ? additionalProductDiscount : discountApplied,
            subscriptionType,
            subscriptionMonth,
            hostName,
            waivedUnit,
            hasParentChildHost,
            currentTab: activeTabKeyValue,
            prebuyUnits: prebuyUnits,
            unifiedInboxStatus: unifiedInboxStatus,
            addOnStatuses: addOnStatuses,
            chargedByFunction: pricingModel.chargedByFunctionsOnly ? [...new Set(systemProduct.flatMap(p => p.functions || []))] : false,
            invoiceDueDate: formattedInvoice.invoiceDueDate
          });
          if (res) {
            // console.log(res, 1);
            if (res.pdfBlob) {
              const url = URL.createObjectURL(res.pdfBlob); // Create the URL from the Blob
              blobRef.current = res.pdfBlob; // Store the Blob in a ref
              let fileName = `${invoiceRef}_${host.name}.pdf`;

              setPdfUrl(url); // Use res.url from the response
              setIsModalOpen(true); // Open the modal
              setTotalAmount(res.GrandTotal);
              setPdfFileName(fileName);
              setGenerateAlert(true);
              setGeneratedPDF(true);
              setInvoiceSnapshot(res.invoiceSnapshot);
              setHasSnapshot(true);
            } else {
              setGenerateFailedMessage(res.message);
              setGenerateFailedAlert(true);
              setGenerateAlert(false);
            }
            setIsLoading(false);
          } else {
            console.error('Received unexpected response type:', res);
          }
        } else {
          setIsLoading(false);
          const errorMessages = Object.keys(err)
            .map(key => {
              return err[key] && Array.isArray(err[key])
                ? err[key]
                    .map(errorObj => (errorObj.errors && Array.isArray(errorObj.errors) ? errorObj.errors.map(err => err.message).join(', ') : ''))
                    .filter(message => message)
                    .join(', ')
                : '';
            })
            .filter(message => message)
            .join(', ');

          notification.error({
            message: 'Validation Error',
            description: errorMessages
          });
        }
      });
    } catch (error) {
      console.error('Error updating unit configurations:', error);
    }
  };

  const addProduct = () => {
    setProductFields([...productFields, counter]);
    setCounter(counter + 1);
  };

  const handleRemoveProductField = index => {
    const updatedProductFields = productFields.filter((_, idx) => _ !== index);
    setProductFields(updatedProductFields);

    //Restore back product in selection
    setSelectedProducts(prevSelected => {
      const filteredProducts = prevSelected.filter(item => item.fieldIndex !== index);
      // console.log(
      //   getFieldValue('unitForHostProtect') === undefined && (manuallyCreatedUnitForHostProtect && manuallyCreatedUnitForHostProtect.length === 0),
      //   100
      // );
      if (
        filteredProducts.length === 0 &&
        getFieldValue('unitForHostProtect') === undefined &&
        (manuallyCreatedUnitForHostProtect && manuallyCreatedUnitForHostProtect.length === 0)
      ) {
        setFieldsValue({
          excludeSystemSubscription: false
        });
      }
      return filteredProducts;
    });

    const updatedValues = { ...form.getFieldsValue() };
    delete updatedValues[`otherProducts[${index}]`];
    delete updatedValues[`quantity[${index}]`];
    setFieldsValue(updatedValues);
  };

  useEffect(() => {
    if (form.getFieldValue('excludeSystemSubscription') === false) {
      console.log('excludeSystemSubscription has been set to false');
    }
  }, [form.getFieldValue('excludeSystemSubscription')]);

  const handleProductSelection = (value, productIndex) => {
    //keep track of index and also value
    form.setFieldsValue({
      [`otherProducts[${productIndex}`]: value
    });

    setSelectedProducts(prevSelected => {
      // Remove the previously selected product for this index, if any
      //if productIndex matches then discard the array in that specific index, to make way for new value
      const filteredSelected = prevSelected.filter(item => item.fieldIndex !== productIndex);

      // Add the new selection with its field index
      if (value) {
        return [...filteredSelected, { ...value, fieldIndex: productIndex }];
      }
      const paddedRefNumber = String(host.additionalProductInvoiceNumber).padStart(3, '0');
      setInvoiceNo(`PINV-SVC-${moment(monthWithYear).format('YYMM')}-${paddedRefNumber}`);

      return filteredSelected;
    });
  };

  const handleDiscountChange = value => {
    form.setFieldsValue({
      [`discountApplied`]: value
    });
  };

  const handleCheckboxChange = (checked, unitId) => {
    setWaive(prevWaive => ({
      ...prevWaive,
      [unitId]: checked
    }));
  };

  const handleToggleAllWaive = checked => {
    const updated = {};
    inventories.forEach(item => {
      updated[item.unitId] = checked;
      form.setFieldsValue({ [`waive_${item.unitId}`]: checked }); // Update AntD form checkbox state
    });
    setWaive(updated);
    setIsAllWaived(checked);
  };

  const handleSaveInvoice = async sendEmail => {
    setCreateLoading(true);
    try {
      let invoicePDFFile;

      if (generatedPDF && blobRef.current) {
        let file = await uploadPDFFile(blobRef.current, pdfFileName);
        // Invoice PDFs are stored privately; persist the object key so the
        // backend attaches the PDF to the invoice email (no public link).
        invoicePDFFile = [{ name: pdfFileName, key: file.key }];
      } else {
        invoicePDFFile = invoiceFiles;
      }

      formattedInvoice.amount = totalAmount;
      formattedInvoice.files = invoicePDFFile;
      formattedInvoice.emailSent = sendEmail;
      formattedInvoice.invoiceSnapshot = invoiceSnapshot;
      formattedInvoice.invoiceNo = invoiceNo;
      formattedInvoice.currency = pricingModel.currency || 'MYR';

      host.invoiceRemark = form.getFieldValue('invoiceRemark');
      host.invoiceAccount = form.getFieldValue('invoiceAccount');

      await updateHost(id, host);

      if (isEditClicked) {
        await updateInvoice(record._id, formattedInvoice);
        message.success('Update success!');
      } else {
        formattedInvoice.invoiceNo = invoiceNo;
        await createInvoice(formattedInvoice);
        message.success('Invoice is successfully created!');
      }

      if (isCurrentMonth) {
        const uniqueUnitIds = new Set([...Object.keys(modifiedSubscriptionFees), ...Object.keys(changedRemarks)]);

        const unitsToUpdate = Array.from(uniqueUnitIds).reduce((acc, unitId) => {
          const inventoryItem = inventories.find(item => item.unitId === unitId);

          if (inventoryItem) {
            const subscriptionFee = unitId in modifiedSubscriptionFees ? modifiedSubscriptionFees[unitId] : inventoryItem.unitConfig.subscriptionFee;

            const newConfig = {
              unitType: inventoryItem.unitConfig.unitType,
              renewalDate: inventoryItem.unitConfig.renewalDate || null,
              subscriptionMode: inventoryItem.unitConfig.subscriptionMode,
              subscriptionFee: subscriptionFee,
              isFree: inventoryItem.unitConfig.isFree,
              primary: inventoryItem.unitPrimary ? inventoryItem.unitPrimary._id : null,
              isActive: inventoryItem.unitConfig.isActive,
              remark: changedRemarks.hasOwnProperty(unitId) ? changedRemarks[unitId] : inventoryItem.unitConfig.remark
            };
            const hasChanges =
              newConfig.subscriptionFee !== inventoryItem.unitConfig.subscriptionFee || newConfig.remark !== inventoryItem.unitConfig.remark;

            if (hasChanges) {
              acc.push({ unitId, newConfig });
            }
          } else {
            console.warn(`Inventory item with unitId ${unitId} not found.`);
          }

          return acc;
        }, []);

        await Promise.all(unitsToUpdate.map(({ unitId, newConfig }) => putUpdateUnitConfig(unitId, newConfig, true)));
      }
    } catch (ex) {
      message.error(ex.toString());
      console.error(ex);
    } finally {
      setCreateLoading(false);
    }
  };

  const addManualUnit = () => {
    const newUnit = {
      key: `manual-${Date.now()}`,
      unit: '',
      ownerName: '',
      icOwner: '',
      address: ''
    };
    setManuallyCreatedUnitForHostProtect([...manuallyCreatedUnitForHostProtect, newUnit]);
  };

  const checkAndSetExcludeSystemSubscription = () => {
    const isManuallyCreatedUnitsEmpty = manuallyCreatedUnitForHostProtect.length === 0;
    const isSelectedUnitsEmpty = selectedUnits.length === 0;
    const isSelectedProductsEmpty = selectedProducts.length === 0;

    // console.log(isManuallyCreatedUnitsEmpty, 33);
    // console.log(isSelectedUnitsEmpty, 44);
    // console.log(isSelectedProductsEmpty, 55);

    // console.log(isManuallyCreatedUnitsEmpty && isSelectedUnitsEmpty && isSelectedProductsEmpty, 22);

    if (
      (manuallyCreatedUnitForHostProtect.length > 0 || selectedUnits.length > 0) &&
      (activeTabKey === 'hostProtect' || selectedProducts.length === 0)
    ) {
      const paddedRefNumber = String(hostProtectInvoiceRef).padStart(3, '0');
      setInvoiceNo(`PINV-HP-${moment(monthWithYear).format('YYMM')}-${paddedRefNumber}`);
    }

    // If all arrays are empty, set excludeSystemSubscription to false
    if (host) {
      if (isManuallyCreatedUnitsEmpty && isSelectedUnitsEmpty) {
        if (!isSelectedProductsEmpty) {
          if (host.additionalProductInvoiceNumber) {
            const paddedRefNumber = String(host.additionalProductInvoiceNumber).padStart(3, '0');
            setInvoiceNo(`PINV-SVC-${moment(monthWithYear).format('YYMM')}-${paddedRefNumber}`);
          } else {
            setInvoiceNo(null);
          }
        }
      }
      if (isManuallyCreatedUnitsEmpty && isSelectedUnitsEmpty && isSelectedProductsEmpty) {
        setFieldsValue({ excludeSystemSubscription: false });
        setInvoiceNo(hostInvoice);
      } else {
        setFieldsValue({ excludeSystemSubscription: true });
      }
    }
  };

  // useEffect hook to monitor changes to selectedUnits, manuallyCreatedUnitForHostProtect, and selectedProducts
  useEffect(() => {
    checkAndSetExcludeSystemSubscription();
  }, [selectedUnits, manuallyCreatedUnitForHostProtect, selectedProducts]);

  // Call this function after updating the states
  const removeManualUnit = unitKey => {
    setManuallyCreatedUnitForHostProtect(prevUnits => {
      const updatedUnits = prevUnits.filter(unit => unit.key !== unitKey);
      return updatedUnits;
    });
  };

  const handleUnitChange = value => {
    setSelectedUnits(value);
  };

  const constructColumns = (unitSourceSelection, unitTypes, planSelection) => {
    const YES_NO_SELECTIONS = [{ label: 'Yes', value: true }, { label: 'No', value: false }];

    return [
      {
        ...constructColumn('Unit', 'unitName', { width: 150, fixed: 'left' }),
        ...constructColumnFilterSearch('unitName', 'Search Unit'),
        render: (text, record) => {
          if (record.isUnitDeleted) {
            return (
              <>
                {text}{' '}
                <Tooltip title="This unit has been deleted">
                  <Icon type="question-circle-o" />
                </Tooltip>
              </>
            );
          } else {
            return text;
          }
        },
        sorter: (a, b) => a.unitName.localeCompare(b.unitName)
      },
      {
        ...constructColumn('Property', 'propertyName', { width: 150 }),
        ...constructColumnFilterSearch('propertyName', 'Search Property'),
        sorter: (a, b) => a.propertyName.localeCompare(b.propertyName)
      },
      {
        ...constructColumn('Host', 'hostName', { width: 150 }),
        ...constructColumnFilterSearch('hostName', 'Search Host'),
        sorter: (a, b) => a.propertyName.localeCompare(b.propertyName)
      },
      {
        ...constructColumn('Host Protect', 'isProtectionActive', { width: 120 }),
        render: text => (text ? 'Yes' : 'No')
      },
      {
        ...constructColumn('Active', 'unitConfig.isActive', { width: 120 }),
        ...constructColumnFilterRadio('unitConfig.isActive', YES_NO_SELECTIONS),
        render: isActive => (isActive ? 'Yes' : 'No')
      },
      {
        ...constructColumn('Plan', 'planNameLabel', { key: 'planName', hasAggregationSorter: true, width: 120 }),
        ...constructColumnFilterRadio('planName', planSelection)
      },
      {
        ...constructColumn('Identity', 'unitConfig.unitTypeLabel', { key: 'unitConfig.unitType', width: 120 }),
        ...constructColumnFilterRadio('unitConfig.unitType', unitTypes)
      },
      {
        ...constructColumn('Parent/ Primary', 'parentPrimaryUnitName', { width: 120 }),
        sorter: (a, b) => {
          const nameA = a.parentPrimaryUnitName || '';
          const nameB = b.parentPrimaryUnitName || '';
          return nameA.localeCompare(nameB);
        }
      },
      {
        ...constructColumn('Occupancy Rate (%)', 'occupancyRate', { hasSorter: true, width: 125 }),
        render: (text, record) => {
          return (
            <>
              {Number((record.occupancyRate || 0) * 100).toFixed(0)}
              {' %'}
            </>
          );
        }
      },
      {
        ...constructColumn('FOC', 'unitConfig.isFree', { width: 100 }),
        ...constructColumnFilterRadio('unitConfig.isFree', YES_NO_SELECTIONS),
        render: isFreeOfCharge => (isFreeOfCharge ? 'Yes' : 'No')
      },
      {
        ...constructColumn('Renewal', 'unitConfig.renewal', { key: 'unitConfig.renewalDate', hasAggregationSorter: true, width: 120 })
      },
      {
        ...constructColumn('Created Date', 'unitCreatedAt', { width: 140 }),
        render: (text, record) => {
          return <>{moment(record.unitCreatedAt).format('DD-MMM-YYYY HH:mm')}</>;
        },
        sorter: (a, b) => new Date(a.unitCreatedAt) - new Date(b.unitCreatedAt)
      },
      {
        ...constructColumn('Deleted Date', 'unitDeletedAt', { width: 120 }),
        render: (text, record) => {
          if (!!record.isUnitDeleted) {
            return <>{moment(record.unitDeletedAt).format('DD-MMM-YYYY HH:mm')}</>;
          } else {
            return <>{' - '}</>;
          }
        },
        sorter: (a, b) => {
          const dateA = a.unitDeletedAt ? new Date(a.unitDeletedAt) : new Date(0);
          const dateB = b.unitDeletedAt ? new Date(b.unitDeletedAt) : new Date(0);
          return dateA - dateB;
        }
      },
      {
        ...constructColumn('Subs Fee', 'unitConfig.subscriptionFee', { isRM: true, width: 120 }),
        render: (text, record) => {
          return (
            <Form.Item>
              {getFieldDecorator(`subscriptionFee_${record.unitId}`, {
                initialValue: subscriptionFees[record.unitId]
              })(
                <InputNumber
                  disabled={shouldDisableButton}
                  min={1}
                  placeholder="Enter subscription fee"
                  onChange={value => {
                    handleSubscriptionFeeChange(record.unitId, value);
                    form.setFieldsValue({ [`subscriptionFee_${record.unitId}`]: value });
                  }}
                  style={{ width: '100%' }}
                />
              )}
            </Form.Item>
          );
        }
      },
      {
        ...constructColumn('Unit Remark', 'unitConfig.remark', { width: 120 }),
        render: (text, record) => {
          return (
            <Form.Item>
              {getFieldDecorator(`remark_${record.unitId}`, {
                initialValue: remarks[record.unitId] || ''
              })(
                <Input disabled={shouldDisableButton} onChange={e => handleRemarkChange(record.unitId, e.target.value)} placeholder="Enter remark" />
              )}
            </Form.Item>
          );
        }
      },
      {
        title: (
          <>
            Waive{' '}
            <Checkbox
              checked={isAllWaived} // this should be state-controlled
              onChange={e => handleToggleAllWaive(e.target.checked)}
            />
          </>
        ),
        key: 'waive',
        render: (text, record) => (
          <Form.Item>
            {getFieldDecorator(`waive_${record.unitId}`, {
              valuePropName: 'checked',
              initialValue: waive[record.unitId] || false
            })(<Checkbox disabled={shouldDisableButton} onChange={e => handleCheckboxChange(e.target.checked, record.unitId)} />)}
          </Form.Item>
        )
      }
    ];
  };

  const renderProductFields = () =>
    productFields.map(fieldIndex => {
      return (
        <Row gutter={16} style={{ paddingTop: '12px' }} key={fieldIndex}>
          <Col span={12}>
            <Form.Item style={{ width: '100%', textAlign: 'center', marginBottom: '0px' }}>
              {getFieldDecorator(`otherProducts[${fieldIndex}]`, {
                rules: [{ required: true, message: 'Please select a product' }]
              })(
                <Select
                  placeholder="Select a product"
                  style={{ width: '100%' }}
                  onChange={value => handleProductSelection(value, fieldIndex)}
                  labelInValue
                >
                  {productData
                    .filter(
                      product =>
                        !selectedProducts.some(sp => sp.key === product._id && sp.fieldIndex !== fieldIndex) &&
                        !product.categoryParent.includes('system') &&
                        !product.categoryParent.includes('hostProtect')
                    )
                    .map(product => (
                      <Select.Option key={product._id} value={product._id}>
                        {product.name}
                      </Select.Option>
                    ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item>
              {getFieldDecorator(`quantity[${fieldIndex}]`, {
                rules: [{ required: true, message: 'Please Input Quantity' }]
              })(<InputNumber placeholder="0" style={{ width: '100%' }} min={1} />)}
            </Form.Item>
          </Col>
          <Col span={1}>
            <Popconfirm
              title="Are you sure you want to remove this product?"
              onConfirm={() => handleRemoveProductField(fieldIndex)}
              okText="Yes"
              cancelText="No"
            >
              <Button className="booking-form-danger" shape="circle" type="danger">
                <Icon type="minus" />
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      );
      //checkbox
    });
  const renderPrebuyFields = () =>
    prebuyFields.map(fieldIndex => {
      return (
        <Row gutter={16} style={{ paddingTop: '12px' }} key={fieldIndex}>
          <Col span={12}>
            <Form.Item style={{ width: '100%', textAlign: 'center', marginBottom: '0px' }}>
              {getFieldDecorator(`prebuy_product[${fieldIndex}]`, {
                rules: [{ required: true, message: 'Please select a product' }]
              })(
                <Select
                  placeholder="Select Plan"
                  style={{ width: '100%' }}
                  onChange={value => handlePrebuyProductSelection(value, fieldIndex)}
                  labelInValue
                >
                  {getSystemProducts()
                    .filter(product => {
                      if (selectedPrebuyProducts.some(sp => sp.key === product._id && sp.fieldIndex !== fieldIndex)) return false;
                      const funcs = product.functions || [];
                      if (!funcs.includes('hostConnect')) return true;
                      // If existingHostOption is true, exclude all subscription add-on variants entirely
                      if (pricingModel.existingHostOption === true) return !funcs.includes('subscriptionAddOn');
                      return unifiedInboxStatus === 'active' ? funcs.includes('subscriptionAddOn') : !funcs.includes('subscriptionAddOn');
                    })
                    .map(product => (
                      <Option key={product._id} value={product._id}>
                        {product.name}
                      </Option>
                    ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item>
              {getFieldDecorator(`prebuy_quantity[${fieldIndex}]`, {
                rules: [{ required: true, message: 'Please Input Quantity' }],
                initialValue: 1
              })(<InputNumber placeholder="Qty" style={{ width: '100%' }} min={1} />)}
            </Form.Item>
          </Col>
          <Col span={1}>
            <Popconfirm
              title="Are you sure you want to remove this prebuy field?"
              onConfirm={() => handleRemovePrebuyField(fieldIndex)}
              okText="Yes"
              cancelText="No"
            >
              <Button className="booking-form-danger" shape="circle" type="danger">
                <Icon type="minus" />
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      );
    });

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  const handleBackClick = () => {
    history.goBack(); // Go back to the previous page
  };

  const isNotCurrentMonth = !isCurrentMonth;
  const isPricingModelEmptyOrLoading = pricingModel.length === 0 || isLoading;
  const isInvoiceNoEmpty = !invoiceNo;
  const shouldDisableButton = isPricingModelEmptyOrLoading || !invoiceNumberValidation || isInvoiceNoEmpty || createLoading || addOnRecordMissing;
  const handleTabChange = key => {
    setActiveTabKey(key); // Update the active tab when changed
  };

  const handleMainTabChange = key => {
    setMainTabKey(key); // Update the main tab when changed
  };

  const handleSaveUnitConfig = async () => {
    if (isCurrentMonth) {
      const uniqueUnitIds = new Set([...Object.keys(modifiedSubscriptionFees), ...Object.keys(changedRemarks)]);

      // Prepare a list of units to be updated
      const unitsToUpdate = Array.from(uniqueUnitIds).reduce((acc, unitId) => {
        const inventoryItem = inventories.find(item => item.unitId === unitId);

        if (inventoryItem) {
          // Use the modified subscription fee if it's explicitly defined, even if it's null
          const subscriptionFee = unitId in modifiedSubscriptionFees ? modifiedSubscriptionFees[unitId] : inventoryItem.unitConfig.subscriptionFee;

          const newConfig = {
            unitType: inventoryItem.unitConfig.unitType,
            renewalDate: inventoryItem.unitConfig.renewalDate || null,
            subscriptionMode: inventoryItem.unitConfig.subscriptionMode,
            subscriptionFee: subscriptionFee,
            isFree: inventoryItem.unitConfig.isFree,
            primary: inventoryItem.unitPrimary ? inventoryItem.unitPrimary._id : null,
            isActive: inventoryItem.unitConfig.isActive,
            remark: changedRemarks.hasOwnProperty(unitId) ? changedRemarks[unitId] : inventoryItem.unitConfig.remark
          };

          // Check if either subscriptionFee or remark has changed
          const hasChanges =
            newConfig.subscriptionFee !== inventoryItem.unitConfig.subscriptionFee || newConfig.remark !== inventoryItem.unitConfig.remark;

          if (hasChanges) {
            acc.push({ unitId, newConfig });
          }
        } else {
          console.warn(`Inventory item with unitId ${unitId} not found.`);
        }

        return acc;
      }, []);

      // Update units with modified configurations
      await Promise.all(unitsToUpdate.map(({ unitId, newConfig }) => putUpdateUnitConfig(unitId, newConfig, true)));
    }
  };

  // Function to get system products filtered for Entry/Starter/Standard
  const getSystemProducts = () => {
    return systemProduct.filter(product => {
      const productName = product.name.toLowerCase() || '';
      return productName;
    });
  };
  // Handle adding new prebuy row
  const handleAddPrebuyProduct = () => {
    setPrebuyFields(prev => [...prev, prebuyCounter]);
    setPrebuyCounter(prev => prev + 1);
  };
  // Handle prebuy product selection
  const handlePrebuyProductSelection = (value, fieldIndex) => {
    // Update form field
    form.setFieldsValue({
      [`prebuy_product[${fieldIndex}]`]: value
    });

    // Update selected prebuy products state
    setSelectedPrebuyProducts(prevSelected => {
      // Remove the previously selected product for this field index, if any
      const filteredSelected = prevSelected.filter(item => item.fieldIndex !== fieldIndex);

      // Add the new selection with its field index
      if (value) {
        return [...filteredSelected, { ...value, fieldIndex }];
      }

      return filteredSelected;
    });
  };

  // Handle removing prebuy field
  const handleRemovePrebuyField = index => {
    const updatedPrebuyFields = prebuyFields.filter((_, idx) => _ !== index);
    setPrebuyFields(updatedPrebuyFields);

    // Remove from selected prebuy products
    setSelectedPrebuyProducts(prevSelected => prevSelected.filter(item => item.fieldIndex !== index));

    // Remove form values
    const updatedValues = { ...form.getFieldsValue() };
    delete updatedValues[`prebuy_product[${index}]`];
    delete updatedValues[`prebuy_quantity[${index}]`];
    form.setFieldsValue(updatedValues);
  };

  const showConfirm = () => {
    Modal.confirm({
      title: 'Are you sure you want to save unit config?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        handleSaveUnitConfig();
        notification.success({
          message: 'Success',
          description: 'Unit config has been updated.'
        });

        // Your logic for the confirmed action here
      },
      onCancel() {
        console.log('Action cancelled');
      }
    });
  };
  return (
    <MainContainer>
      <BodyContainer>
        {showGenerateAlert && (
          <Alert
            style={{ marginBottom: '10px' }}
            message="Invoice generated successfully, Please save the invoice to update the PDF."
            type="success"
            showIcon
            closable
            onClose={() => setGenerateAlert(false)} // Hide the alert on close
          />
        )}
        {showGenerateFailedAlert && (
          <Alert
            style={{ marginBottom: '10px' }}
            message={generateFailedMessage}
            type="error"
            showIcon
            closable
            onClose={() => setGenerateFailedAlert(false)} // Hide the alert on close
          />
        )}
        <Row justify="space-between" style={{ marginBottom: '20px' }}>
          <Col span={12} style={{ textAlign: 'left' }}>
            <Button type="primary" onClick={handleBackClick}>
              Back
            </Button>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Row justify="end" align="middle">
              <Button disabled={shouldDisableButton || pdfUrl === null} onClick={showModal} type="primary">
                {isEditClicked ? 'Save' : 'Create'}
              </Button>
              <Divider
                type="vertical"
                style={{
                  height: '40px',
                  width: '2px',
                  border: 'none' // Remove default border
                }}
              />
              <Button disabled={shouldDisableButton} onClick={handleGenerate} type="primary">
                Generate
              </Button>

              <Divider
                type="vertical"
                style={{
                  height: '40px',
                  width: '2px',
                  border: 'none' // Remove default border
                }}
              />
              <Button disabled={shouldDisableButton || !pdfUrl} onClick={() => setIsModalOpen(true)} type="primary">
                Preview
              </Button>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Form>
          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Host Field (Placed on top) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* New div containing "Invoice Details" */}
                <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '20px', color: '#1890ff' }}>
                  <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                  Invoice Details
                </div>
                {/* Existing div with Host field */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, justifyContent: 'flex-end' }}>
                  <span style={{ fontWeight: 'bold', marginRight: 8 }}>Host:</span>
                  {getFieldDecorator('hostField', {})(<Input disabled={true} style={{ width: '250px' }} />)}
                </div>
              </div>

              {/* Left Side - All Fields */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
                {/* Pricing Model and Invoice Number on same line */}
                <div style={{ display: 'flex', width: '100%', gap: '40px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '450px' }}>
                    <div style={{ minWidth: '160px', fontWeight: 'bold' }}>Pricing Model</div>
                    <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>: </div>
                    <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>{pricingModel.name || 'No Pricing Model Applied'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ minWidth: '115px', fontWeight: 'bold' }}>Invoice Number</div>
                    <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>: </div>
                    {host && (
                      <span style={{ fontWeight: 'bold', marginLeft: 8 }}>
                        {(() => {
                          const excludeSystemSubscription = form.getFieldValue('excludeSystemSubscription') || false;
                          const hasUnits =
                            (form.getFieldValue('unitForHostProtect') || []).length > 0 || (manuallyCreatedUnitForHostProtect || []).length > 0;
                          if (
                            !invoiceNo &&
                            !(
                              (form.getFieldValue('unitForHostProtect') !== undefined && form.getFieldValue('unitForHostProtect').length > 0) ||
                              (manuallyCreatedUnitForHostProtect && manuallyCreatedUnitForHostProtect.length > 0)
                            ) &&
                            (activeTabKey === 'hostProtect' || selectedProducts.length === 0)
                          ) {
                            if (excludeSystemSubscription) {
                              if (!hasUnits && (activeTabKey === 'hostProtect' || selectedProducts.length === 0)) {
                                return (
                                  <span style={{ color: 'red' }}>
                                    No Additional Product Invoice Number, please configure in{' '}
                                    <a href={`/host/${host._id}/edit`} style={{ color: 'red', textDecoration: 'underline' }}>
                                      Host Form
                                    </a>
                                    .
                                  </span>
                                );
                              }
                            }

                            return (
                              <span style={{ color: 'red' }}>
                                The host does not have an Invoice Number, please configure it in{' '}
                                <a href={`/host/${host._id}/edit`} style={{ color: 'red', textDecoration: 'underline' }}>
                                  Host Form
                                </a>
                              </span>
                            );
                          }

                          if (invoiceNumberValidation === false) {
                            return <span style={{ color: 'red' }}>There are invoices with the same Invoice Number! Contact Tech Team</span>;
                          }
                          const padInvoiceNumber = value => {
                            const match = value.match(/^(\d+)(-?[A-Za-z]*)$/); // Matches digits followed by optional hyphen and letters
                            if (match) {
                              const numericPart = match[1]; // The numeric part
                              const letterPart = match[2] || ''; // The letter part (optional)
                              const paddedNumericPart = numericPart.padStart(3, '0'); // Pad numeric part
                              return `${paddedNumericPart}${letterPart}`; // Combine padded number and letter part
                            }
                            return value; // If no match, return the original value
                          };
                          const pdfType = !(form.getFieldValue('excludeSystemSubscription') || false)
                            ? `PINV-${moment(monthWithYear).format('YYMM')}-${padInvoiceNumber(String(host.invoiceNumber))}`
                            : ((form.getFieldValue('unitForHostProtect') !== undefined && form.getFieldValue('unitForHostProtect').length > 0) ||
                                (manuallyCreatedUnitForHostProtect && manuallyCreatedUnitForHostProtect.length > 0)) &&
                              (activeTabKey === 'hostProtect' || selectedProducts.length === 0)
                            ? (() => {
                                const paddedRefNumber = String(hostProtectInvoiceRef).padStart(3, '0');
                                return `PINV-HP-${moment(monthWithYear).format('YYMM')}-${paddedRefNumber}`;
                              })()
                            : (() => {
                                if (!host.additionalProductInvoiceNumber) {
                                  return (
                                    <span style={{ color: 'red' }}>
                                      No Additional Product Invoice Number, please configure in{' '}
                                      <a href={`/host/${host._id}/edit`} style={{ color: 'red', textDecoration: 'underline' }}>
                                        Host Form
                                      </a>
                                      .
                                    </span>
                                  );
                                }

                                const paddedRefNumber = String(host.additionalProductInvoiceNumber).padStart(3, '0');
                                return `PINV-SVC-${moment(monthWithYear).format('YYMM')}-${paddedRefNumber}`;
                              })();

                          return pdfType;
                        })()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Subscription Type */}
                <div style={{ display: 'flex', width: '100%', gap: '40px' }}>
                  {pricingModel.subscriptionType && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', width: '450px' }}>
                        <div style={{ minWidth: '160px', fontWeight: 'bold' }}>Subscription Type</div>
                        <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>: </div>
                        <span>
                          {form.getFieldValue('excludeSystemSubscription') ? (
                            <span style={{ marginLeft: 8 }}>-</span>
                          ) : pricingModel.subscriptionType ? (
                            <Tag color={pricingModel.subscriptionType === 'yearly' ? 'blue' : 'green'} style={{ marginLeft: 8, fontWeight: 'bold' }}>
                              {capitalizeFirstLetter(pricingModel.subscriptionType)}
                            </Tag>
                          ) : (
                            <Tag color="red" style={{ marginLeft: 8 }}>
                              No Subscription Type
                            </Tag>
                          )}
                        </span>
                      </div>

                      {(host.subscriptionMonth || host.subscriptionMonth === 0) && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ minWidth: '115px', fontWeight: 'bold' }}>Billing Month</div>
                          <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>: </div>
                          <span style={{ fontWeight: 'bold', marginLeft: 8 }}>
                            {MONTHS.find(month => month.code === host.subscriptionMonth).label || '-'}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Generated PDF Type */}
                <div style={{ display: 'flex', width: '100%', gap: '40px' }}>
                  {/* Generate PDF Type */}
                  <div style={{ display: 'flex', alignItems: 'center', width: '450px' }}>
                    <div style={{ minWidth: '160px', fontWeight: 'bold' }}>Generate PDF Type</div>
                    <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>:</div>
                    <span>
                      {(() => {
                        const pdfType = !(form.getFieldValue('excludeSystemSubscription') || false)
                          ? 'System Subscription'
                          : ((getFieldValue('unitForHostProtect') !== undefined && getFieldValue('unitForHostProtect').length > 0) ||
                              (manuallyCreatedUnitForHostProtect && manuallyCreatedUnitForHostProtect.length > 0)) &&
                            (activeTabKey === 'hostProtect' || selectedProducts.length === 0)
                          ? 'HostProtect'
                          : 'Additional Products';

                        const colorMap = {
                          'System Subscription': 'blue',
                          HostProtect: 'gold',
                          'Additional Products': 'purple'
                        };

                        return (
                          <Tag color={colorMap[pdfType] || 'red'} style={{ marginLeft: 8, fontWeight: 'bold' }}>
                            {pdfType}
                          </Tag>
                        );
                      })()}
                    </span>
                  </div>

                  {/* Has Snapshot */}
                  <div style={{ display: 'flex', alignItems: 'center', width: '450px' }}>
                    <div style={{ minWidth: '115px', fontWeight: 'bold' }}>Has Snapshot</div>
                    <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>:</div>
                    <span>
                      {hasSnapshot ? (
                        <Tag color="green" style={{ marginLeft: 8, fontWeight: 'bold' }}>
                          Yes
                        </Tag>
                      ) : (
                        <Tag color="red" style={{ marginLeft: 8, fontWeight: 'bold' }}>
                          No
                        </Tag>
                      )}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', width: '100%', gap: '40px', alignItems: 'center' }}>
                  {pricingModel && pricingModel.name ? (
                    pricingModel.chargedByFunctionsOnly ? (
                      <Alert
                        message={(() => {
                          const uniqueFunctions = [...new Set(systemProduct.flatMap(p => p.functions || []))];
                          const functionLabels = uniqueFunctions.map(funcCode => {
                            const found = billingFunctions.find(bf => bf.value === funcCode);
                            return found ? capitalizeFirstLetter(found.label) : capitalizeFirstLetter(funcCode);
                          });
                          return `This host is being charged with only ${functionLabels.join(', ')}`;
                        })()}
                        type="info"
                        showIcon
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* Unified Inbox toggle (unchanged) */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ minWidth: '160px', fontWeight: 'bold' }}>Unified Inbox</div>
                          <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>: </div>
                          <Switch
                            style={{ marginLeft: 8 }}
                            checked={unifiedInboxStatus === 'active'}
                            disabled={initialUnifiedInboxStatus === 'inactive'}
                            onChange={checked => setUnifiedInboxStatus(checked ? 'active' : 'inactive')}
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                          />
                        </div>

                        {/* Other subscription add-ons (excluding Unified Inbox) */}
                        {subscriptionAddOnProductData
                          .filter(
                            product =>
                              product.categoryParent &&
                              product.categoryParent.includes('subscriptionAddOn') &&
                              !(product.name || '').toLowerCase().includes('unified inbox')
                          )
                          .map(product => {
                            return (
                              <div key={product._id} style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ minWidth: '160px', fontWeight: 'bold' }}>{product.name}</div>
                                <div style={{ width: '12px', textAlign: 'center', fontWeight: 'bold' }}>: </div>
                                <Switch
                                  style={{ marginLeft: 8 }}
                                  checked={addOnStatuses && addOnStatuses[product._id] === 'active'}
                                  onChange={checked => {
                                    const newStatus = checked ? 'active' : 'inactive';
                                    setAddOnStatuses(prev => ({
                                      ...prev,
                                      [product._id]: newStatus
                                    }));
                                  }}
                                  checkedChildren="Active"
                                  unCheckedChildren="Inactive"
                                />
                              </div>
                            );
                          })}
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>
          </Form.Item>
        </Form>
        <Divider />
        <Form.Item label="Invoice Account">
          {getFieldDecorator('invoiceAccount', {
            initialValue: invoiceAccount // Set initial value from state
          })(<Input disabled={shouldDisableButton} placeholder="Enter invoice account" />)}
        </Form.Item>
        <Form.Item label="Invoice Remark">
          {getFieldDecorator('invoiceRemark', {
            initialValue: invoiceRemark // Set initial value from state
          })(<Input.TextArea disabled={shouldDisableButton} placeholder="Enter invoice remark" />)}
        </Form.Item>
        <Divider />

        {/* Main Tabs for Inventories and Prebuy */}
        <Tabs defaultActiveKey="inventories" activeKey={mainTabKey} onChange={handleMainTabChange}>
          {/* Inventories Tab */}
          <TabPane tab="Inventories" key="inventories">
            {Object.keys(alerts).length > 0 && (
              <Alert
                message="Some subscription fees have been changed from their original values."
                type="warning"
                showIcon
                style={{ marginBottom: '10px' }}
              />
            )}
            {addOnRecordMissing && (
              <Alert
                message={`Add-on subscription status for ${moment(monthWithYear, 'YYYY-MM')
                  .subtract(1, 'months')
                  .format('MMMM YYYY')} is missing. Unified Inbox status may be inaccurate. Please contact tech team`}
                type="error"
                showIcon
                style={{ marginBottom: '10px' }}
              />
            )}
            {!pricingModel.chargeableChild && (
              <Alert style={{ marginBottom: '10px' }} message="Child unit price will not be charged." type="warning" showIcon />
            )}
            {pricingModel.onlyBillUnifiedInboxOTAUnits && (
              <Alert
                style={{ marginBottom: '10px' }}
                message={
                  Array.isArray(pricingModel.unifiedInboxOtaKeys) && pricingModel.unifiedInboxOtaKeys.length > 0
                    ? `Only units with active hostConnect for selected OTAs (${pricingModel.unifiedInboxOtaKeys
                        .map(code => {
                          const ota = HOST_CONNECT_OTA_OPTIONS.find(option => option.code === code);
                          return ota ? ota.label : code;
                        })
                        .join(', ')}) will be charged for Unified Inbox.`
                    : 'Only units with selected OTA hostConnect active will be charged for Unified Inbox.'
                }
                type="warning"
                showIcon
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
              {isNotCurrentMonth ? (
                <Tooltip title="Only the current month of unit config can be saved">
                  <Button disabled={isNotCurrentMonth} onClick={showConfirm} type="primary">
                    Save Unit Config
                  </Button>
                </Tooltip>
              ) : (
                <Button onClick={showConfirm} type="primary">
                  Save Unit Config
                </Button>
              )}
            </div>
            <Table
              loading={isLoading || createLoading}
              rowKey={record => record.unitId}
              dataSource={inventories}
              columns={constructColumns(unitSourceSelection, unitTypes, planSelection)}
              scroll={{ x: 2000 }}
            />
          </TabPane>
          {/* Prebuy Tab */}
          <TabPane tab="Pre-Buy" key="prebuy">
            <Form>
              {prebuyFields.length > 0 && (
                <>
                  <Row gutter={16}>
                    <Col span={12}>Plan</Col>
                    <Col span={8}>Quantity</Col>
                    <Col span={4}></Col>
                  </Row>
                </>
              )}
              {renderPrebuyFields()}
              <Button type="dashed" onClick={handleAddPrebuyProduct} style={{ width: '100%' }}>
                <Icon type="plus" /> Add Pre-Buy
              </Button>
            </Form>
          </TabPane>
        </Tabs>
        <Divider />
        <Form.Item label="Additional Charges">
          {/* Tabs for Products and Host Protect */}
          <Tabs defaultActiveKey="additionalProduct" activeKey={activeTabKey} onChange={handleTabChange}>
            {/* Products Tab */}
            <TabPane tab="Products" key="additionalProduct">
              <Form>
                {productFields.length > 0 && (
                  <>
                    <Form.Item label="Discount">
                      {getFieldDecorator(`discountApplied`, {})(
                        <Select
                          placeholder="Select a discount"
                          style={{ width: '100%' }}
                          onChange={value => handleDiscountChange(value)}
                          labelInValue
                        >
                          {discountSelections.map(discount => (
                            <Select.Option key={discount._id} value={discount._id}>
                              {discount._id === 'none'
                                ? `${discount.name}`
                                : `${discount.name} ( -${discount.type === 'percentage' ? `${discount.amount}% ` : `${discount.amount} `})`}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                    <Row justify="start" gutter={8}>
                      <Col span={12}>Product</Col>
                      <Col span={12}>Quantity</Col>
                    </Row>
                  </>
                )}
                {renderProductFields()}
                <Button type="dashed" onClick={addProduct} style={{ width: '100%' }}>
                  <Icon type="plus" /> Add Products
                </Button>
              </Form>
            </TabPane>

            {/* Host Protect Tab */}
            {productData.some(product => product.categoryParent.includes('hostProtect')) && (
              <TabPane tab="Host Protect" key="hostProtect">
                <Form>
                  {/* Selection Dropdown from inventories.unit */}
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item label="Select Unit(s)">
                        {getFieldDecorator('unitForHostProtect', {})(
                          <Select
                            placeholder="Select a unit"
                            style={{ width: '100%' }}
                            mode="multiple"
                            labelInValue // Enable to store both label and value
                            onChange={handleUnitChange}
                          >
                            {inventories.map(inventory => (
                              <Option key={inventory.unitId} value={inventory.unitId}>
                                {inventory.unitName}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Manually Add Units Button */}
                  <Row gutter={16}>
                    <Col span={24} style={{ textAlign: 'right', marginBottom: '12px' }}>
                      <Button type="dashed" onClick={addManualUnit}>
                        Add Unit Manually
                      </Button>
                    </Col>
                  </Row>

                  {/* Table for Units */}
                  {(manuallyCreatedUnitForHostProtect.length > 0 || selectedUnits.length > 0) && (
                    <>
                      <Alert
                        message="Please input the necessary information for Host Protect on the units"
                        type="info"
                        showIcon
                        style={{ marginTop: '12px' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'left', width: '100%' }}>
                        <div
                          style={{
                            marginTop: '8px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            display: 'inline-flex',
                            flexDirection: 'column',
                            width: '100%',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Header Section */}
                          <div
                            style={{
                              display: 'flex',
                              borderBottom: '1px solid #d9d9d9',
                              backgroundColor: '#fafafa'
                            }}
                          >
                            <div style={{ flex: 1, fontWeight: 'bold', padding: '8px', borderRight: '1px solid #d9d9d9' }}>Property Name</div>
                            <div style={{ flex: 1, fontWeight: 'bold', padding: '8px', borderRight: '1px solid #d9d9d9' }}>Unit Name</div>
                            <div style={{ flex: 1, fontWeight: 'bold', padding: '8px', borderRight: '1px solid #d9d9d9' }}>Owner's Name</div>
                            <div style={{ flex: 1, fontWeight: 'bold', padding: '8px', borderRight: '1px solid #d9d9d9' }}>IC Owner</div>
                            <div style={{ flex: 1, fontWeight: 'bold', padding: '8px' }}>Address</div>
                          </div>

                          {/* Data Rows */}
                          {getFieldValue('unitForHostProtect') &&
                            getFieldValue('unitForHostProtect').map((unit, index) => {
                              const inventoryItem = inventories.find(inv => inv.unitId === unit.key);
                              return (
                                <div
                                  key={unit.key}
                                  style={{
                                    display: 'flex',
                                    borderBottom: '1px solid #d9d9d9'
                                  }}
                                >
                                  {/* Property Name Column as Input */}
                                  <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9', position: 'relative' }}>
                                    <Form.Item>
                                      {getFieldDecorator(`propertyName[${unit.key}]`, {
                                        rules: [{ required: true, message: 'Please enter the property name' }],
                                        initialValue: inventoryItem ? inventoryItem.propertyName : ''
                                      })(<Input placeholder="Property Name" style={{ width: '100%' }} />)}
                                    </Form.Item>
                                  </div>
                                  {/* Unit Name Column as Input */}
                                  <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9', position: 'relative' }}>
                                    <Form.Item>
                                      {getFieldDecorator(`unitName[${unit.key}]`, {
                                        rules: [{ required: true, message: 'Please enter the unit name' }],
                                        initialValue: unit.label
                                      })(<Input placeholder="Unit Name" style={{ width: '100%' }} />)}
                                    </Form.Item>
                                  </div>

                                  {/* Owner's Name Column */}
                                  <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9' }}>
                                    <Form.Item>
                                      {getFieldDecorator(`ownerName[${unit.key}]`, {
                                        rules: [{ required: true, message: 'Please enter the owner’s name' }],
                                        initialValue: inventoryItem ? inventoryItem.ownerName : ''
                                      })(<Input placeholder="Owner's Name" style={{ width: '100%' }} />)}
                                    </Form.Item>
                                  </div>

                                  {/* IC Owner Column */}
                                  <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9' }}>
                                    <Form.Item>
                                      {getFieldDecorator(`icOwner[${unit.key}]`, {
                                        rules: [{ required: true, message: 'Please enter IC owner' }]
                                      })(<Input placeholder="IC Owner" style={{ width: '100%' }} />)}
                                    </Form.Item>
                                  </div>

                                  {/* Address Column */}
                                  <div style={{ flex: 1, padding: '8px' }}>
                                    <Form.Item>
                                      {getFieldDecorator(`address[${unit.key}]`, {
                                        rules: [{ required: true, message: 'Please enter the address' }]
                                      })(<Input placeholder="Address" style={{ width: '100%' }} />)}
                                    </Form.Item>
                                  </div>
                                </div>
                              );
                            })}
                          {manuallyCreatedUnitForHostProtect.map((unit, index) => {
                            const inventoryItem = inventories.find(inv => inv.unitId === unit.key);
                            return (
                              <div
                                key={unit.key}
                                style={{
                                  display: 'flex',
                                  borderBottom: '1px solid #d9d9d9'
                                }}
                              >
                                {/* Property Name Column as Input */}
                                <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9', position: 'relative' }}>
                                  <Form.Item>
                                    {getFieldDecorator(`propertyName[${unit.key}]`, {})(<Input placeholder="Unit Name" style={{ width: '95%' }} />)}
                                  </Form.Item>
                                  <Button
                                    type="danger"
                                    size="small"
                                    style={{ position: 'absolute', top: '1%', right: '1%', height: '20px' }}
                                    onClick={() => removeManualUnit(unit.key)}
                                  >
                                    -
                                  </Button>
                                </div>

                                {/* Unit Name Column as Input */}
                                <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9', position: 'relative' }}>
                                  <Form.Item>
                                    {getFieldDecorator(`unitName[${unit.key}]`, {})(<Input placeholder="Unit Name" style={{ width: '95%' }} />)}
                                  </Form.Item>
                                </div>

                                {/* Owner's Name Column */}
                                <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9' }}>
                                  <Form.Item>
                                    {getFieldDecorator(`ownerName[${unit.key}]`, {
                                      rules: [{ required: true, message: 'Please enter the owner’s name' }]
                                    })(<Input placeholder="Owner's Name" style={{ width: '100%' }} />)}
                                  </Form.Item>
                                </div>

                                {/* IC Owner Column */}
                                <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #d9d9d9' }}>
                                  <Form.Item>
                                    {getFieldDecorator(`icOwner[${unit.key}]`, {
                                      rules: [{ required: true, message: 'Please enter IC owner' }]
                                    })(<Input placeholder="IC Owner" style={{ width: '100%' }} />)}
                                  </Form.Item>
                                </div>

                                {/* Address Column */}
                                <div style={{ flex: 1, padding: '8px' }}>
                                  <Form.Item>
                                    {getFieldDecorator(`address[${unit.key}]`, {
                                      rules: [{ required: true, message: 'Please enter the address' }]
                                    })(<Input placeholder="Address" style={{ width: '100%' }} />)}
                                  </Form.Item>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              </TabPane>
            )}
          </Tabs>

          {/* Exclude System Subscription Checkbox outside of Tabs */}
          {(productFields.length > 0 ||
            (getFieldValue('unitForHostProtect') && getFieldValue('unitForHostProtect').length > 0) ||
            manuallyCreatedUnitForHostProtect.length > 0) && (
            <Form.Item>
              {getFieldDecorator('excludeSystemSubscription', {
                valuePropName: 'checked',
                initialValue: true
              })(<Checkbox disabled={true}>Exclude System Subscription In Invoice</Checkbox>)}
            </Form.Item>
          )}
        </Form.Item>
        <Modal
          title="Confirm Save"
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={null} // Disable default footer
        >
          {isEditClicked ? (
            <p>Are you sure you want to save the invoice?</p>
          ) : (
            <>
              <p>Are you sure you want to create the invoice?</p>
              <p>Email : {hostEmail}</p>
            </>
          )}
          <Divider /> {/* Add a divider between the content and buttons */}
          {/* Custom buttons */}
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Button type="primary" style={{ width: '100%' }} onClick={() => handleOk(false)}>
                {isEditClicked ? 'Update' : 'Create'}
              </Button>
            </Col>
            <Col span={24}>
              <Button type="primary" style={{ width: '100%' }} onClick={() => handleOk(true)}>
                {isEditClicked ? 'Update and Send Email' : 'Create and Send Email'}
              </Button>
            </Col>
          </Row>
        </Modal>
      </BodyContainer>
      <Modal
        title="Preview Invoice"
        visible={isModalOpen}
        onCancel={handleCloseModal}
        footer={
          <Button onClick={handleCloseModal} type="primary">
            Close
          </Button>
        }
        width="80%" // Adjust width as needed
        style={{ top: '1%' }} // Position modal higher by reducing the top margin
        bodyStyle={{ height: '90vh', padding: '0' }} // Full viewport height minus the header
      >
        {pdfUrl && <iframe src={pdfUrl} width="100%" height="100%" />}
      </Modal>
    </MainContainer>
  );
};

export default Form.create()(withRouter(PreviewInventory));
